import { useEffect, useMemo, useReducer, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FinanceAccessDeniedState, FinancePageHeader } from "@/components/finance/ui";
import { canAccessFinanceRoute } from "@/finance/financeNav";
import { trpc } from "@/lib/trpc";
import { hasCapability, type FinanceRole } from "@shared/finance/capabilities";
import { AlertTriangle, CheckCircle2, Eye, FileCheck2, Layers, RefreshCw, Search, ShieldAlert } from "lucide-react";
import {
  createLegacyTaxBulkViewState,
  createLegacyTaxPreviewFingerprint,
  filterLegacyTaxRows,
  invalidateCommonRetentionAttestation,
  isSnapshotSelectionCurrent,
  legacyTaxBulkViewReducer,
  resolveLegacyTaxChoice,
  splitLegacyTaxChunks,
  type LegacyTaxBulkDefaults,
  type LegacyTaxBulkRow,
  type LegacyTaxRowDraft,
  type LegacyTaxSnapshotSelection,
  type LegacyTaxTreatment,
} from "@/finance/legacyTaxReconciliationBulk";

type Treatment = LegacyTaxTreatment;
type PreviewRow = {
  id: number;
  canApply: boolean;
  reasons: string[];
  requiresDocumentAttestation?: boolean;
  invoiceDocumentAvailable?: boolean;
  calculation: null | {
    taxableBase: string;
    taxAmount: string;
    withholdingAmount: string;
    payableAmount: string;
    treatment: Treatment;
  };
  snapshotToken: string | null;
  documentAttestationToken?: string | null;
  collectionOrigin?: "none" | "ledger" | "snapshot" | "unknown";
};
type SelectionInput = {
  id: number;
  treatment: Treatment;
  invoiceWithholdingAmount: string;
  reason: string;
  snapshotToken: string;
  documentAttestation?: { attested: true; matchedBase: string; matchedTotal: string };
  documentAttestationToken?: string;
};
type PreviewState = { rows: PreviewRow[]; fingerprint: string; basisFingerprint: string; parts: number };
type ProgressState = { phase: "preview" | "apply"; part: number; parts: number; rowCount: number };
type PreviewIssue = { part: number; parts: number; message: string };
type RowResult = { id: number; status: string; message?: string; part: number; parts: number };
type RowChoice = LegacyTaxRowDraft;

const money = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });
const euro = (value: unknown) => value == null || value === "" ? "—" : money.format(Number(value));
const treatmentLabel: Record<Treatment, string> = {
  iva_21: "IVA general 21%",
  reverse_charge: "Inversión del sujeto pasivo (clasificada)",
};
const statusLabels: Record<string, string> = {
  pending_collection: "Pendiente de cobro",
  invoiced: "Facturada",
  sent_to_client: "Enviada",
  collected: "Cobrada",
  cancelled: "Cancelada",
};
const emptyDefaults = (): LegacyTaxBulkDefaults => ({ withholdingAmount: "", withholdingAttested: false, documentAmountsAttested: false, reason: "" });
const hasKnownFiscalWithholding = (value: unknown) => value !== null && value !== undefined && String(value).trim() !== "";
const cents = (value: unknown) => {
  if (value == null || value === "") return 0;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
};
const euroCents = (value: number) => money.format(value / 100);
const messageFrom = (error: unknown) => error instanceof Error ? error.message.split("\n")[0] : "La solicitud no pudo completarse.";

export default function LegacyTaxReconciliation() {
  const { user } = useAuth();
  const [view, dispatch] = useReducer(legacyTaxBulkViewReducer, undefined, createLegacyTaxBulkViewState);
  const [rowChoices, setRowChoices] = useState<Record<number, RowChoice>>({});
  const [batchDefaults, setBatchDefaults] = useState<LegacyTaxBulkDefaults>(emptyDefaults);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewIssue, setPreviewIssue] = useState<PreviewIssue | null>(null);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [results, setResults] = useState<RowResult[]>([]);
  const query = trpc.financeLegacyTaxReconciliation.list.useQuery();
  const previewMutation = trpc.financeLegacyTaxReconciliation.preview.useMutation();
  const applyMutation = trpc.financeLegacyTaxReconciliation.confirm.useMutation();
  const canWrite = Boolean(user && hasCapability(user.role as FinanceRole, "finance.write"));
  const allRows = query.data ?? [];
  const rowsById = useMemo(() => new Map(allRows.map((row) => [row.id, row])), [allRows]);
  const filteredRows = useMemo(
    () => filterLegacyTaxRows(allRows, view.filters),
    [allRows, view.filters],
  );
  const selectedEntries = useMemo(
    () => view.selection.flatMap((selection) => {
      const row = rowsById.get(selection.id);
      return row ? [{ selection, row }] : [];
    }),
    [rowsById, view.selection],
  );
  const selectedIds = useMemo(() => selectedEntries.map(({ selection }) => selection.id), [selectedEntries]);
  const selectedRows = useMemo(() => selectedEntries.map(({ row }) => row), [selectedEntries]);
  const isSelectionCurrent = isSnapshotSelectionCurrent(view.selection, allRows);
  const choicesFor = (row: LegacyTaxBulkRow) => resolveLegacyTaxChoice(
    row,
    rowChoices[row.id] ?? {},
    batchDefaults,
  );
  const fiscalSelectionInputs: SelectionInput[] = selectedEntries.flatMap(({ selection, row }) => {
    const choice = choicesFor(row);
    if (!choice.treatment) return [];
    return [{
      id: row.id,
      treatment: choice.treatment,
      invoiceWithholdingAmount: choice.withholdingAmount.trim(),
      reason: choice.reason.trim(),
      snapshotToken: selection.snapshotToken,
    }];
  });
  const documentObservationInputs = selectedEntries.map(({ row }) => {
    const choice = choicesFor(row);
    return {
      id: row.id,
      matchedBase: choice.documentBase.trim(),
      matchedTotal: choice.documentTotal.trim(),
      attested: choice.documentAmountsAttested,
    };
  });
  const previewBasisFingerprint = createLegacyTaxPreviewFingerprint(
    view.selection,
    selectedRows as LegacyTaxBulkRow[],
    fiscalSelectionInputs,
  );
  const previewBasisIsCurrent = Boolean(preview && preview.basisFingerprint === previewBasisFingerprint && isSelectionCurrent);
  const previewBasisRowsById = new Map((previewBasisIsCurrent && preview ? preview.rows : []).map((row) => [row.id, row]));
  const selectionInputs: SelectionInput[] = fiscalSelectionInputs.map((input) => {
    const row = rowsById.get(input.id);
    const choice = row ? choicesFor(row) : null;
    const priorCalculation = previewBasisRowsById.get(input.id)?.calculation;
    const requiresDocumentAttestation = previewBasisRowsById.get(input.id)?.requiresDocumentAttestation ?? row?.requiresDocumentAttestation;
    if (requiresDocumentAttestation
      && choice?.documentAmountsAttested
      && choice.documentBase.trim() !== ""
      && choice.documentTotal.trim() !== ""
      && priorCalculation) {
      return {
        ...input,
        documentAttestation: {
          attested: true,
          matchedBase: choice.documentBase.trim(),
          matchedTotal: choice.documentTotal.trim(),
        },
      };
    }
    return input;
  });
  const previewFingerprint = createLegacyTaxPreviewFingerprint(
    view.selection,
    selectedRows as LegacyTaxBulkRow[],
    [...selectionInputs, { documentObservations: documentObservationInputs }],
  );
  const previewIsCurrent = Boolean(preview && preview.fingerprint === previewFingerprint && isSelectionCurrent);
  const validPreviewRows = previewIsCurrent ? preview!.rows.filter((row) => row.canApply && row.calculation) : [];
  const invalidPreviewRows = previewIsCurrent ? preview!.rows.filter((row) => !row.canApply || !row.calculation) : [];
  const displayedPreviewRows = previewBasisIsCurrent ? preview!.rows : [];
  const calculatedPreviewRows = displayedPreviewRows.filter((row) => row.calculation);
  const previewTotals = calculatedPreviewRows.reduce((total, row) => ({
    base: total.base + cents(row.calculation?.taxableBase),
    tax: total.tax + cents(row.calculation?.taxAmount),
    withholding: total.withholding + cents(row.calculation?.withholdingAmount),
    payable: total.payable + cents(row.calculation?.payableAmount),
  }), { base: 0, tax: 0, withholding: 0, payable: 0 });
  const eligibleCount = filteredRows.filter((row) => row.canApply).length;
  const excludedCount = filteredRows.length - eligibleCount;
  const commonRetentionAffectedCount = selectedEntries.filter(({ row }) => {
    const draft = rowChoices[row.id] ?? {};
    return !hasKnownFiscalWithholding(row.fiscalWithholdingAmount) && !draft.withholdingAmount?.trim();
  }).length;
  const documentAttestationAffectedCount = selectedEntries.filter(({ row }) =>
    (previewBasisRowsById.get(row.id)?.requiresDocumentAttestation ?? row.requiresDocumentAttestation)
      && (previewBasisRowsById.get(row.id)?.invoiceDocumentAvailable ?? row.invoiceDocumentAvailable)
      && Boolean(previewBasisRowsById.get(row.id)?.calculation),
  ).length;
  const documentAttestationCommonCount = selectedEntries.filter(({ row }) =>
    (previewBasisRowsById.get(row.id)?.requiresDocumentAttestation ?? row.requiresDocumentAttestation)
      && (previewBasisRowsById.get(row.id)?.invoiceDocumentAvailable ?? row.invoiceDocumentAvailable)
      && rowChoices[row.id]?.documentAmountsAttested === undefined
      && Boolean(previewBasisRowsById.get(row.id)?.calculation),
  ).length;
  const documentObservationIncompleteCount = selectedEntries.filter(({ row }) => {
    const choice = choicesFor(row);
    return (previewBasisRowsById.get(row.id)?.requiresDocumentAttestation ?? row.requiresDocumentAttestation)
      && (previewBasisRowsById.get(row.id)?.invoiceDocumentAvailable ?? row.invoiceDocumentAvailable)
      && Boolean(previewBasisRowsById.get(row.id)?.calculation)
      && (!choice.documentBase.trim() || !choice.documentTotal.trim());
  }).length;
  const isBusy = progress !== null;
  const canPreview = selectedEntries.length > 0
    && selectedEntries.length === view.selection.length
    && isSelectionCurrent
    && selectionInputs.length === selectedEntries.length
    && selectedEntries.every(({ row }) => {
      const choice = choicesFor(row);
      return row.canApply
        && Boolean(choice.treatment)
        && choice.withholdingAmount.trim() !== ""
        && choice.reason.trim().length >= 10
        && choice.reason.trim().length <= 500
        && !choice.needsCommonRetentionAttestation;
    });

  useEffect(() => {
    if (!query.data || query.isFetching) return;
    dispatch({ type: "reconcile_rows", rows: query.data as LegacyTaxBulkRow[] });
  }, [query.data, query.isFetching]);

  if (!user || !canAccessFinanceRoute("/accounting/legacy-tax-reconciliation", user.role)) {
    return <DashboardLayout><FinanceAccessDeniedState title="Acceso no disponible" message="Esta herramienta temporal requiere acceso de lectura de Finanzas." /></DashboardLayout>;
  }

  const invalidatePreview = () => {
    setPreview(null);
    setPreviewIssue(null);
  };
  const resetDocumentAttestations = () => {
    setBatchDefaults((current) => ({ ...current, documentAmountsAttested: false }));
    setRowChoices((current) => Object.fromEntries(Object.entries(current).map(([id, choice]) => [
      id,
      { ...choice, documentAmountsAttested: undefined },
    ])));
  };
  const clearSelection = () => {
    dispatch({ type: "clear_selection" });
    setRowChoices({});
    setBatchDefaults(emptyDefaults());
    invalidatePreview();
    setResults([]);
  };
  const changeFilters = (patch: Partial<typeof view.filters>) => {
    const next = { ...view.filters, ...patch };
    if (next.search === view.filters.search && next.status === view.filters.status && next.eligibility === view.filters.eligibility) return;
    dispatch({ type: "filter_changed", filters: next });
    setRowChoices({});
    setBatchDefaults(emptyDefaults());
    invalidatePreview();
    setResults([]);
  };
  const toggleSelected = (row: LegacyTaxBulkRow, checked: boolean) => {
    dispatch({ type: "toggle", row, checked });
    setBatchDefaults((current) => invalidateCommonRetentionAttestation(current));
    if (!checked) {
      setRowChoices((current) => {
        const next = { ...current };
        delete next[row.id];
        return next;
      });
    }
    invalidatePreview();
  };
  const selectAllFilteredEligible = () => {
    dispatch({ type: "select_all_filtered", rows: filteredRows as LegacyTaxBulkRow[] });
    setBatchDefaults((current) => invalidateCommonRetentionAttestation(current));
    setRowChoices({});
    invalidatePreview();
  };
  const clearSelectedChoice = (id: number) => {
    setRowChoices((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    invalidatePreview();
  };
  const setRowChoice = (id: number, patch: Partial<RowChoice>) => {
    setRowChoices((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
    resetDocumentAttestations();
    invalidatePreview();
  };
  const setBatchTreatment = (value: string) => {
    setBatchDefaults((current) => ({ ...current, treatment: value ? value as Treatment : undefined }));
    resetDocumentAttestations();
    invalidatePreview();
  };
  const setBatchReason = (value: string) => {
    setBatchDefaults((current) => ({ ...current, reason: value }));
    resetDocumentAttestations();
    invalidatePreview();
  };
  const setBatchRetention = (value: string) => {
    setBatchDefaults((current) => ({ ...current, withholdingAmount: value, withholdingAttested: false }));
    resetDocumentAttestations();
    invalidatePreview();
  };
  const setBatchRetentionAttestation = (checked: boolean) => {
    setBatchDefaults((current) => ({ ...current, withholdingAttested: checked }));
    invalidatePreview();
  };
  const setBatchDocumentAttestation = (checked: boolean) => {
    setBatchDefaults((current) => ({ ...current, documentAmountsAttested: checked }));
    setPreviewIssue(null);
  };
  const setRowDocumentAttestation = (id: number, checked: boolean) => {
    setRowChoices((current) => ({ ...current, [id]: { ...current[id], documentAmountsAttested: checked } }));
    setPreviewIssue(null);
  };
  const setRowDocumentObservation = (id: number, field: "documentBase" | "documentTotal", value: string) => {
    setRowChoices((current) => ({
      ...Object.fromEntries(Object.entries(current).map(([rowId, choice]) => [
        rowId,
        { ...choice, documentAmountsAttested: undefined },
      ])),
      [id]: { ...current[id], [field]: value, documentAmountsAttested: undefined },
    }));
    setBatchDefaults((current) => ({ ...current, documentAmountsAttested: false }));
    setPreviewIssue(null);
  };
  const refreshList = async () => {
    clearSelection();
    setProgress(null);
    await query.refetch();
  };

  const runPreview = async () => {
    if (!canPreview) return;
    const parts = splitLegacyTaxChunks(selectionInputs, 100);
    if (!parts.length) return;
    setResults([]);
    setPreview(null);
    setPreviewIssue(null);
    const allPreviewRows: PreviewRow[] = [];
    for (let index = 0; index < parts.length; index += 1) {
      setProgress({ phase: "preview", part: index + 1, parts: parts.length, rowCount: parts[index].length });
      try {
        const partRows = await previewMutation.mutateAsync({ selections: parts[index] }) as PreviewRow[];
        const expectedIds = parts[index].map((item) => item.id);
        const returnedIds = new Set(partRows.map((item) => item.id));
        if (partRows.length !== parts[index].length
          || returnedIds.size !== partRows.length
          || expectedIds.some((id) => !returnedIds.has(id))) {
          throw new Error("La respuesta de preview no coincide exactamente con los IDs enviados.");
        }
        allPreviewRows.push(...partRows);
      } catch (error) {
        setPreviewIssue({ part: index + 1, parts: parts.length, message: messageFrom(error) });
        setProgress(null);
        return;
      }
    }
    setPreview({ rows: allPreviewRows, fingerprint: previewFingerprint, basisFingerprint: previewBasisFingerprint, parts: parts.length });
    setProgress(null);
  };

  const applyValid = async () => {
    if (!previewIsCurrent || !preview || validPreviewRows.length === 0 || isBusy) return;
    const previewById = new Map(preview.rows.map((row) => [row.id, row]));
    const validInputs = selectionInputs.filter((item) => previewById.get(item.id)?.canApply).flatMap((item) => {
      const previewRow = previewById.get(item.id)!;
      if (!item.documentAttestation) return [item];
      if (!previewRow.documentAttestationToken) return [];
      return [{ ...item, documentAttestation: { ...item.documentAttestation, token: previewRow.documentAttestationToken } }];
    });
    if (validInputs.length !== validPreviewRows.length) {
      setPreviewIssue({ part: 1, parts: preview.parts, message: "Falta la huella de atestación documental; vuelve a previsualizar." });
      return;
    }
    const parts = splitLegacyTaxChunks(validInputs, 100);
    if (!parts.length) return;
    const allResults: RowResult[] = [];
    for (let index = 0; index < parts.length; index += 1) {
      setProgress({ phase: "apply", part: index + 1, parts: parts.length, rowCount: parts[index].length });
      try {
        const response = await applyMutation.mutateAsync({ selections: parts[index] });
        const expectedIds = parts[index].map((item) => item.id);
        const returnedIds = new Set(response.map((item) => item.id));
        if (response.length !== parts[index].length
          || returnedIds.size !== response.length
          || expectedIds.some((id) => !returnedIds.has(id))) {
          throw new Error("La respuesta de confirmación no coincide exactamente con los IDs enviados.");
        }
        const byId = new Map(response.map((item) => [item.id, item]));
        for (const input of parts[index]) {
          const item = byId.get(input.id);
          allResults.push(item
            ? { id: item.id, status: item.status, message: "message" in item ? item.message : undefined, part: index + 1, parts: parts.length }
            : { id: input.id, status: "response_missing", message: "La respuesta del servidor omitió esta fila; verifica su estado antes de continuar.", part: index + 1, parts: parts.length });
        }
        setResults([...allResults]);
      } catch (error) {
        for (const input of parts[index]) {
          allResults.push({ id: input.id, status: "result_unconfirmed", message: `El resultado de esta parte no pudo confirmarse: ${messageFrom(error)}`, part: index + 1, parts: parts.length });
        }
        const laterParts = parts.slice(index + 1);
        for (let laterOffset = 0; laterOffset < laterParts.length; laterOffset += 1) {
          for (const later of laterParts[laterOffset]) {
            allResults.push({ id: later.id, status: "not_attempted", message: "No se envió porque una parte anterior terminó sin respuesta confirmada.", part: index + 2 + laterOffset, parts: parts.length });
          }
        }
        setResults([...allResults]);
        break;
      }
    }
    setProgress(null);
    dispatch({ type: "clear_selection" });
    setRowChoices({});
    setBatchDefaults(emptyDefaults());
    invalidatePreview();
    await query.refetch();
  };

  const filteredStatusOptions = Array.from(new Set([
    "pending_collection", "invoiced", "sent_to_client", "collected", "cancelled",
    ...allRows.map((row) => String(row.status)),
    ...(view.filters.status !== "all" ? [view.filters.status] : []),
  ]));
  const selectionIsStale = view.selection.length > 0 && !isSelectionCurrent;
  const selectedMissingFromQuery = view.selection.length - selectedEntries.length;
  const previewRowById = previewBasisRowsById;
  const resultsPassed = results.filter((result) => result.status === "applied" || result.status === "already_applied").length;
  const resultsUncertain = results.filter((result) => result.status === "result_unconfirmed" || result.status === "response_missing").length;
  const resultsNotAttempted = results.filter((result) => result.status === "not_attempted").length;
  const resultHasErrors = results.some((result) => !["applied", "already_applied"].includes(result.status));

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-7xl min-w-0 space-y-5">
        <FinancePageHeader
          eyebrow="Finanzas · herramienta temporal"
          eyebrowIcon={FileCheck2}
          title="Regularización fiscal histórica"
          description="Completa manualmente el desglose fiscal de facturas legacy identificadas. La base económica del contrato/hito no cambia; fiscalidad, exigible y evidencia de cobro permanecen separados."
          badgeText="Temporal · selección manual"
          primaryAction={{ label: "Actualizar lista", icon: RefreshCw, onClick: refreshList, variant: "outline" }}
        />

        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="flex min-w-0 gap-3 p-4 text-sm text-amber-950">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="min-w-0 space-y-1 [overflow-wrap:anywhere]">
              <p className="font-semibold">No se infiere el tratamiento fiscal ni la retención</p>
              <p>Selecciona IVA general 21% o inversión del sujeto pasivo clasificada. La retención se contrasta con la factura: una retención fiscal histórica conocida se conserva; NULL no se convierte en cero ni se copia la retención contractual. El cero común exige atestación documental manual.</p>
              <p>Se clasifican manualmente solo filas identificadas y coherentes en pendiente de cobro, facturada, enviada o cobrada. Cualquier pago/evento en estados abiertos, canceladas, pre-factura, rectificativas, duplicadas o incoherentes quedan excluidas con motivo.</p>
              <p>Todo PDF identificado requiere que el operador introduzca la base y el total leídos del documento y los ateste contra el preview. Una base documental distinta de la planificada queda bloqueada para revisión económica; una garantía contractual no es retención fiscal. La app no altera ni reemite el documento.</p>
              <p>Cobrada permite clasificación fiscal-only: estado, importe/método/fecha/actor snapshot, comisiones, intereses y ledger quedan idénticos. Origen ledger/snapshot/unknown se conserva; no se crean cobros, caja, deuda ni reapertura.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Buscar y filtrar cohortes</CardTitle></CardHeader>
          <CardContent className="grid min-w-0 gap-3 md:grid-cols-3">
            <label className="min-w-0 space-y-1 text-sm">
              <span>Factura, cliente, contrato o hito</span>
              <div className="relative min-w-0">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="min-w-0 pl-9" value={view.filters.search} disabled={isBusy} onChange={(event) => changeFilters({ search: event.target.value })} placeholder="Buscar texto o número" aria-label="Buscar factura, cliente, contrato o hito" />
              </div>
            </label>
            <label className="min-w-0 space-y-1 text-sm">
              <span>Estado de factura</span>
              <Select value={view.filters.status} disabled={isBusy} onValueChange={(value) => changeFilters({ status: value })}>
                <SelectTrigger className="w-full min-w-0" aria-label="Filtrar por estado de factura"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {filteredStatusOptions.map((status) => <SelectItem key={status} value={status}>{statusLabels[status] ?? status}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="min-w-0 space-y-1 text-sm">
              <span>Elegibilidad en esta campaña</span>
              <Select value={view.filters.eligibility} disabled={isBusy} onValueChange={(value) => changeFilters({ eligibility: value as typeof view.filters.eligibility })}>
                <SelectTrigger className="w-full min-w-0" aria-label="Filtrar elegibilidad"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Elegibles y solo lectura</SelectItem>
                  <SelectItem value="eligible">Solo elegibles actuales</SelectItem>
                  <SelectItem value="excluded">Solo lectura / excluidas</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </CardContent>
        </Card>

        <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-live="polite">
          <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Candidatas cargadas</p><p className="text-xl font-semibold">{allRows.length}</p></CardContent></Card>
          <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Coinciden con filtros</p><p className="text-xl font-semibold">{filteredRows.length}</p></CardContent></Card>
          <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Seleccionables del filtro</p><p className="text-xl font-semibold">{eligibleCount}</p></CardContent></Card>
          <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Solo lectura / excluidas</p><p className="text-xl font-semibold">{excludedCount}</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Selección y valores comunes del lote</CardTitle></CardHeader>
          <CardContent className="min-w-0 space-y-4">
            <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1 space-y-1 text-sm">
                <p className="font-medium">Seleccionadas: {selectedEntries.length} de {eligibleCount} seleccionables en el filtro actual.</p>
                <p className="text-muted-foreground">“Seleccionar todas” captura ahora los IDs y huellas visibles; una recarga no agrega facturas nuevas. Cualquier cambio de filtro limpia la selección para evitar filas ocultas.</p>
              </div>
              <div className="flex w-full min-w-0 max-w-full flex-col gap-2 sm:flex-row sm:flex-wrap lg:flex-1 lg:justify-end">
                <Button className="w-full min-w-0 max-w-full shrink whitespace-normal text-left [overflow-wrap:anywhere] sm:w-auto sm:flex-1 lg:flex-none" type="button" variant="outline" disabled={isBusy || eligibleCount === 0} onClick={selectAllFilteredEligible}>
                  <Layers className="mr-2 h-4 w-4" />Seleccionar todas las {eligibleCount} seleccionables del filtro
                </Button>
                <Button className="w-full min-w-0 max-w-full shrink whitespace-normal sm:w-auto sm:flex-none" type="button" variant="outline" disabled={isBusy || selectedEntries.length === 0} onClick={clearSelection}>Limpiar selección</Button>
              </div>
            </div>

            <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3">
              <label className="min-w-0 space-y-1 text-sm">
                <span>Tratamiento común (sin valor por defecto)</span>
                <Select value={batchDefaults.treatment ?? "none"} disabled={isBusy} onValueChange={(value) => setBatchTreatment(value === "none" ? "" : value)}>
                  <SelectTrigger className="w-full min-w-0" aria-label="Tratamiento fiscal común"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Elegir explícitamente</SelectItem>
                    <SelectItem value="iva_21">IVA general 21%</SelectItem>
                    <SelectItem value="reverse_charge">Inversión del sujeto pasivo (clasificada)</SelectItem>
                  </SelectContent>
                </Select>
                <span className="block text-xs text-muted-foreground">Se usa como tratamiento común; conserva la corrección individual de cada fila.</span>
              </label>
              <label className="min-w-0 space-y-1 text-sm">
                <span>Retención fiscal común para datos desconocidos (EUR)</span>
                <Input inputMode="decimal" value={batchDefaults.withholdingAmount} disabled={isBusy} placeholder="Vacío hasta verificar" onChange={(event) => setBatchRetention(event.target.value)} aria-label="Retención fiscal común" />
                <span className="block text-xs text-muted-foreground">Inicialmente vacío; solo completa filas sin retención fiscal conocida. Escribir 0 no se infiere.</span>
              </label>
              <label className="min-w-0 space-y-1 text-sm md:col-span-2 xl:col-span-1">
                <span>Motivo documental común (10–500 caracteres)</span>
                <Textarea className="min-h-20 w-full min-w-0 resize-y" value={batchDefaults.reason} disabled={isBusy} maxLength={500} placeholder="Contraste realizado contra documentos originales" onChange={(event) => setBatchReason(event.target.value)} aria-label="Motivo documental común" />
                <span className="block text-xs text-muted-foreground">Se registra por fila; una nota común evita repetirla factura a factura.</span>
              </label>
            </div>

            {batchDefaults.withholdingAmount.trim() !== "" ? (
              <div className="flex min-w-0 items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                <Checkbox id="legacy-tax-common-retention-attestation" checked={batchDefaults.withholdingAttested} disabled={isBusy || commonRetentionAffectedCount === 0} onCheckedChange={(checked) => setBatchRetentionAttestation(checked === true)} />
                <label htmlFor="legacy-tax-common-retention-attestation" className="min-w-0 [overflow-wrap:anywhere]">
                  Atestiguo tras revisar la documentación que la retención fiscal común indicada aplica a las {commonRetentionAffectedCount} seleccionada(s) sin dato fiscal conocido. Las retenciones históricas conocidas se conservarán sin cambios.
                </label>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {query.isLoading ? <p className="py-8 text-center text-sm text-muted-foreground">Cargando facturas legacy…</p> : null}
        {query.isError ? <Card className="border-destructive"><CardContent className="p-4 text-sm">No se pudo cargar la lista: {query.error.message}</CardContent></Card> : null}
        {!query.isLoading && !query.isError && filteredRows.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">No hay candidatas que coincidan con la búsqueda y los filtros actuales.</CardContent></Card>
        ) : null}

        {filteredRows.map((row) => {
          const draft = rowChoices[row.id] ?? {};
          const choice = choicesFor(row);
          const currentPreview = previewRowById.get(row.id);
          const requiresDocumentAttestation = currentPreview?.requiresDocumentAttestation ?? row.requiresDocumentAttestation;
          const invoiceDocumentAvailable = currentPreview?.invoiceDocumentAvailable ?? row.invoiceDocumentAvailable;
          const invoiceDocumentUrl = invoiceDocumentAvailable ? row.invoiceDocumentUrl : null;
          const selectable = Boolean(row.canApply);
          const knownRetention = hasKnownFiscalWithholding(row.fiscalWithholdingAmount);
          const rowRetentionValue = knownRetention
            ? String(row.fiscalWithholdingAmount)
            : draft.withholdingAmount ?? (choice.withholdingSource === "common" ? choice.withholdingAmount : "");
          const placeholderReason = batchDefaults.reason.trim().length >= 10
            ? `Se aplicará la nota común (${batchDefaults.reason.trim().length} caracteres)`
            : "Qué se verificó contra la factura original";
          return (
            <Card key={row.id} data-testid={`legacy-tax-row-${row.id}`} className={!selectable ? "border-amber-300" : ""}>
              <CardContent className="min-w-0 space-y-4 p-4 sm:p-5">
                <div className="flex min-w-0 items-start gap-3">
                  <Checkbox
                    aria-label={`Seleccionar factura legacy ${row.id}`}
                    checked={selectedIds.includes(row.id)}
                    disabled={isBusy || !selectable}
                    onCheckedChange={(checked) => toggleSelected(row, checked === true)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <h2 className="min-w-0 max-w-full font-semibold [overflow-wrap:anywhere]">{row.customerName || "Cliente sin identificar"}</h2>
                      <Badge variant={row.status === "collected" ? "secondary" : "outline"}>{statusLabels[String(row.status)] ?? String(row.status)}</Badge>
                      <Badge variant={selectable ? "outline" : "destructive"}>{selectable
                        ? requiresDocumentAttestation ? "Seleccionable · requiere contraste" : row.status === "collected" ? "Clasificación-only · cerrada" : "Seleccionable"
                        : "Solo lectura / excluida"}</Badge>
                      {selectedIds.includes(row.id) ? <Badge variant="secondary">Seleccionada · snapshot fijado</Badge> : null}
                    </div>
                    <p className="mt-1 min-w-0 text-sm text-muted-foreground [overflow-wrap:anywhere]">{row.contractCode ?? "Sin contrato verificable"} · {row.contractTitle ?? "—"} · {row.installationName ?? "Sin instalación directa"}</p>
                    <p className="mt-1 min-w-0 text-sm [overflow-wrap:anywhere]">Hito: {row.label} · Factura: {row.invoiceNumber ?? "sin número"} · Fecha: {row.invoiceDate ? new Date(String(row.invoiceDate)).toLocaleDateString("es-ES") : "sin fecha"}</p>
                    {invoiceDocumentUrl ? (
                      <a className="mt-1 inline-flex min-w-0 items-center gap-1 text-sm text-ods-navigation underline [overflow-wrap:anywhere]" href={String(invoiceDocumentUrl)} target="_blank" rel="noopener noreferrer">
                        <Eye className="h-4 w-4 shrink-0" />Abrir documento identificado para contraste manual
                      </a>
                    ) : invoiceDocumentAvailable ? (
                      <p className="mt-1 text-sm text-muted-foreground">Documento asociado; abre el hito desde su expediente para contrastarlo. La atestación no habilita modificación del archivo.</p>
                    ) : requiresDocumentAttestation ? (
                      <p className="mt-1 text-sm text-amber-800">No se encuentra un documento adjunto en esta fila; no atestes hasta localizar y contrastar el documento identificado.</p>
                    ) : null}
                    <div className="mt-2 grid min-w-0 gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
                      <p>Base histórica sin IVA: <strong>{euro(row.baseAmount)}</strong></p>
                      <p>Tratamiento guardado: <strong>{row.taxTreatment === "iva_21" ? "IVA 21%" : row.taxTreatment === "reverse_charge" ? "Inversión SP clasificada" : "Sin clasificar"}</strong></p>
                      <p>Cuota IVA guardada: <strong>{euro(row.taxAmount)}</strong></p>
                      <p>Exigible actual: <strong>{euro(row.historicalAmount)}</strong></p>
                      <p>Retención contractual: <strong>{euro(row.contractualRetentionAmount)}</strong></p>
                      <p>Retención fiscal guardada: <strong>{euro(row.fiscalWithholdingAmount)}</strong></p>
                    </div>
                    {row.collectionAmountDisclosure ? <p className="mt-2 min-w-0 rounded-md bg-muted/60 p-2 text-sm [overflow-wrap:anywhere]">{row.collectionAmountDisclosure}</p> : null}
                  </div>
                </div>

                {selectable ? (
                  <div className="grid min-w-0 gap-3 rounded-md border bg-muted/20 p-3 md:grid-cols-2 xl:grid-cols-4">
                    <label className="min-w-0 space-y-1 text-sm">
                      <span>Tratamiento fiscal</span>
                      <Select value={choice.treatment ?? "none"} disabled={isBusy} onValueChange={(value) => setRowChoice(row.id, { treatment: value === "none" ? undefined : value as Treatment })}>
                        <SelectTrigger className="h-auto min-h-9 w-full min-w-0 whitespace-normal data-[size=default]:h-auto" aria-label={`Tratamiento fiscal de factura ${row.id}`}><SelectValue className="min-w-0 flex-1 !line-clamp-none whitespace-normal [overflow-wrap:anywhere]" placeholder="Elegir explícitamente" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Usar tratamiento común / elegir</SelectItem>
                          <SelectItem value="iva_21">IVA general 21%</SelectItem>
                          <SelectItem value="reverse_charge">Inversión del sujeto pasivo (clasificada)</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="block text-xs text-muted-foreground">Una selección común no elimina esta excepción individual.</span>
                    </label>
                    <label className="min-w-0 space-y-1 text-sm">
                      <span>Retención fiscal de factura (EUR)</span>
                      <Input inputMode="decimal" value={rowRetentionValue} disabled={isBusy || knownRetention} placeholder="Vacío hasta verificar" onChange={(event) => setRowChoice(row.id, { withholdingAmount: event.target.value })} aria-label={`Retención fiscal de factura ${row.id}`} />
                      {knownRetention
                        ? <span className="block text-xs text-muted-foreground">Valor histórico preservado. El writer actual no permite cambiarlo en esta campaña.</span>
                        : <span className="block text-xs text-muted-foreground">Vacío no es cero; escribe el valor contrastado o usa el común con atestación.</span>}
                    </label>
                    <label className="min-w-0 space-y-1 text-sm md:col-span-2">
                      <span>Excepción de motivo documental (10–500 caracteres)</span>
                      <Input value={draft.reason ?? ""} disabled={isBusy} maxLength={500} placeholder={placeholderReason} onChange={(event) => setRowChoice(row.id, { reason: event.target.value })} aria-label={`Motivo documental de factura ${row.id}`} />
                      <span className="block text-xs text-muted-foreground">Déjalo vacío para aplicar el motivo común; un texto aquí sustituye solo esta fila.</span>
                    </label>
                    <div className="md:col-span-2 xl:col-span-4">
                      {(draft.treatment || draft.withholdingAmount !== undefined || draft.reason || draft.documentAmountsAttested !== undefined || draft.documentBase !== undefined || draft.documentTotal !== undefined) ? (
                        <Button type="button" size="sm" variant="ghost" disabled={isBusy} onClick={() => clearSelectedChoice(row.id)}>Restablecer datos comunes en esta fila</Button>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="min-w-0 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 [overflow-wrap:anywhere]">
                    <p className="font-medium">Solo lectura en esta campaña</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5">{(row.exclusionReasons ?? []).map((reason, index) => <li key={index}>{reason}</li>)}</ul>
                  </div>
                )}

                {currentPreview ? (
                  <div className={currentPreview.canApply && currentPreview.calculation ? "min-w-0 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm" : "min-w-0 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"}>
                    {currentPreview.calculation ? (
                      <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                        <p className="[overflow-wrap:anywhere]">Tratamiento: <strong>{treatmentLabel[currentPreview.calculation.treatment]}</strong></p>
                        <p>Base sin IVA: <strong>{euro(currentPreview.calculation.taxableBase)}</strong></p>
                        <p>Cuota IVA: <strong>{euro(currentPreview.calculation.taxAmount)}</strong></p>
                        <p>Retención fiscal: <strong>{euro(currentPreview.calculation.withholdingAmount)}</strong></p>
                        <p>Exigible: <strong>{euro(currentPreview.calculation.payableAmount)}</strong></p>
                      </div>
                    ) : null}
                    {row.status === "collected" && currentPreview.calculation ? (
                      <p className="mt-2 rounded bg-white/70 p-2 [overflow-wrap:anywhere]">Cohorte cobrada y cerrada ({row.collectionOrigin ?? "origen desconocido"}): el exigible fiscal mostrado no se convierte en saldo abierto, ingreso ni cobro. Estado, evidencia de pago y ledger permanecen intactos.</p>
                    ) : null}
                    {currentPreview.requiresDocumentAttestation && currentPreview.calculation && selectedIds.includes(row.id) ? (
                      <div className="mt-3 min-w-0 space-y-2 rounded border bg-white/80 p-2">
                        <div className="grid min-w-0 gap-2 sm:grid-cols-2">
                          <label className="min-w-0 space-y-1 text-sm">
                            <span>Base imponible exacta leída del PDF (EUR)</span>
                            <Input inputMode="decimal" value={choice.documentBase} disabled={isBusy || !invoiceDocumentAvailable} placeholder="Introducir base del documento" onChange={(event) => setRowDocumentObservation(row.id, "documentBase", event.target.value)} aria-label={`Base documental de factura ${row.id}`} />
                          </label>
                          <label className="min-w-0 space-y-1 text-sm">
                            <span>Total exacto leído del PDF (EUR)</span>
                            <Input inputMode="decimal" value={choice.documentTotal} disabled={isBusy || !invoiceDocumentAvailable} placeholder="Introducir total del documento" onChange={(event) => setRowDocumentObservation(row.id, "documentTotal", event.target.value)} aria-label={`Total documental de factura ${row.id}`} />
                          </label>
                        </div>
                        <div className="flex min-w-0 items-start gap-2 rounded border p-2 text-sm">
                          <Checkbox
                            id={`legacy-tax-document-attestation-${row.id}`}
                            checked={choice.documentAmountsAttested}
                            disabled={isBusy || !invoiceDocumentAvailable || !choice.documentBase.trim() || !choice.documentTotal.trim()}
                            onCheckedChange={(checked) => setRowDocumentAttestation(row.id, checked === true)}
                          />
                          <label htmlFor={`legacy-tax-document-attestation-${row.id}`} className="min-w-0 [overflow-wrap:anywhere]">
                            He leído el documento {row.invoiceNumber}: atestiguo que la base introducida coincide con {euro(currentPreview.calculation.taxableBase)} y el total introducido coincide con {euro(currentPreview.calculation.payableAmount)} del preview. El PDF queda intacto.
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {!currentPreview.canApply || !currentPreview.calculation ? (
                      <ul className="list-disc space-y-1 pl-5">{currentPreview.reasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          );
        })}

        {selectedEntries.length > 0 ? (
          <div className="sticky bottom-3 z-10 flex min-w-0 flex-col gap-2 rounded-lg border bg-background/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-1 text-sm">
              <p>Seleccionadas: {selectedEntries.length}; ID y huella fijados. Cada cambio de valor o dato invalida el preview.</p>
              {selectionIsStale ? <p role="alert" className="text-amber-800">Alguna factura cambió o dejó de ser elegible desde la selección. Limpia y vuelve a seleccionar para obtener una huella nueva.</p> : null}
              {selectedMissingFromQuery > 0 ? <p role="alert" className="text-amber-800">{selectedMissingFromQuery} selección(es) ya no están en el listado; no se enviarán.</p> : null}
              {commonRetentionAffectedCount > 0 && batchDefaults.withholdingAmount.trim() !== "" && !batchDefaults.withholdingAttested
                ? <p role="alert" className="text-amber-800">Atestigua el valor común después de revisar documentación o informa cada retención desconocida por fila.</p>
                : null}
              {previewBasisIsCurrent && !previewIsCurrent && documentAttestationAffectedCount > 0
                ? <p role="alert" className="text-amber-800">El importe del preview se conserva para el contraste, pero la atestación cambió. Reprevisualiza toda la selección antes de poder confirmar.</p>
                : null}
              {selectionInputs.some((item) => item.reason.length < 10) && batchDefaults.reason.trim().length < 10
                ? <p className="text-muted-foreground">Añade una nota común verificable (10–500) o una excepción por cada fila.</p>
                : null}
            </div>
            <Button className="w-full min-w-0 max-w-full shrink whitespace-normal text-left [overflow-wrap:anywhere] sm:w-auto sm:max-w-[45%]" disabled={!canPreview || isBusy} onClick={runPreview}>
              {progress?.phase === "preview" ? `Previsualizando parte ${progress.part}/${progress.parts}…` : previewBasisIsCurrent && !previewIsCurrent ? "Reprevisualizar atestación y selección" : "Previsualizar toda la selección"}
            </Button>
          </div>
        ) : null}

        {progress ? (
          <Card aria-live="polite" className="border-primary">
            <CardContent className="flex min-w-0 items-start gap-3 p-4 text-sm">
              <Layers className="mt-0.5 h-5 w-5 shrink-0" />
              <p className="min-w-0 [overflow-wrap:anywhere]">
                {progress.phase === "preview" ? "Previsualizando" : "Confirmando"} parte {progress.part} de {progress.parts} ({progress.rowCount} filas; máximo 100 por petición), secuencialmente. {progress.phase === "apply" ? "Cada fila mantiene el resultado/auditoría del writer; un fallo puede dejar éxito parcial." : "Aún no se ha confirmado ninguna parte."}
              </p>
            </CardContent>
          </Card>
        ) : null}

        {previewIssue ? (
          <Card role="alert" className="border-destructive"><CardContent className="p-4 text-sm">Preview incompleto en parte {previewIssue.part}/{previewIssue.parts}; no se puede confirmar. {previewIssue.message}</CardContent></Card>
        ) : null}
        {preview && !previewIsCurrent ? (
          <Card role="alert" className="border-amber-300"><CardContent className="p-4 text-sm">El preview quedó invalidado por cambios de selección o datos. Limpia/actualiza y vuelve a previsualizar antes de confirmar.</CardContent></Card>
        ) : null}

        {previewBasisIsCurrent && preview ? (
          <Card className="border-primary">
            <CardHeader className="pb-3"><CardTitle className="text-base">Preview completo · {preview.rows.length} filas en {preview.parts} parte(s)</CardTitle></CardHeader>
            <CardContent className="min-w-0 space-y-4">
              {!previewIsCurrent ? <p role="alert" className="rounded bg-amber-50 p-2 text-sm">Atestación documental cambiada después de este cálculo. Las cifras se muestran para contraste; no son confirmables hasta terminar otra previsualización.</p> : null}
              <p className="text-sm">Importes calculados en {calculatedPreviewRows.length} fila(s). {previewIsCurrent ? `Válidas: ${validPreviewRows.length}; conflictos/excluidas: ${invalidPreviewRows.length}.` : `La respuesta previa registró ${preview.rows.filter((row) => row.canApply).length} fila(s) aplicable(s) sin la nueva atestación.`} La selección conserva IDs/tokens explícitos.</p>
              <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-5" aria-label="Totales agregados del preview">
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Facturas calculadas</p><p className="font-semibold">{calculatedPreviewRows.length}</p></div>
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Base sin IVA</p><p className="font-semibold">{euroCents(previewTotals.base)}</p></div>
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">IVA</p><p className="font-semibold">{euroCents(previewTotals.tax)}</p></div>
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Retención fiscal</p><p className="font-semibold">{euroCents(previewTotals.withholding)}</p></div>
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Exigible</p><p className="font-semibold">{euroCents(previewTotals.payable)}</p></div>
              </div>
              {documentAttestationCommonCount > 0 ? (
                <div className="flex min-w-0 items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                  <Checkbox id="legacy-tax-common-document-attestation" checked={batchDefaults.documentAmountsAttested} disabled={isBusy || documentObservationIncompleteCount > 0} onCheckedChange={(checked) => setBatchDocumentAttestation(checked === true)} />
                  <label htmlFor="legacy-tax-common-document-attestation" className="min-w-0 [overflow-wrap:anywhere]">
                    He leído individualmente e introducido la base y el total de cada PDF seleccionado; atestiguo que ambos importes coinciden al céntimo con las cifras de su preview. Solo aplica a las filas sin excepción individual y con ambos valores presentes. Un total histórico desconocido o igual a la base solo se completa/corrige con esta coincidencia documental exacta.
                  </label>
                </div>
              ) : null}
              <p className="text-sm text-muted-foreground">La confirmación ejecutará {previewIsCurrent ? splitLegacyTaxChunks(validPreviewRows, 100).length : 0} parte(s) secuenciales, con éxito parcial posible según resultado por fila; no es una transacción atómica del lote.</p>
              <Button className="w-full min-w-0 max-w-full shrink whitespace-normal text-left [overflow-wrap:anywhere] sm:w-auto" disabled={!previewIsCurrent || !canWrite || validPreviewRows.length === 0 || isBusy} onClick={applyValid}>
                {progress?.phase === "apply" ? `Confirmando parte ${progress.part}/${progress.parts}…` : `Confirmar explícitamente ${validPreviewRows.length} factura(s)`}
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {previewMutation.isError && !previewIssue ? <Card role="alert" className="border-destructive"><CardContent className="p-3 text-sm">{previewMutation.error.message}</CardContent></Card> : null}
        {applyMutation.isError && results.length === 0 ? <Card role="alert" className="border-destructive"><CardContent className="p-3 text-sm">{applyMutation.error.message}</CardContent></Card> : null}
        {results.length > 0 ? (
          <Card className={resultHasErrors ? "border-amber-400" : "border-emerald-300"}>
            <CardHeader className="pb-3"><CardTitle className="text-base">Última confirmación · resultado por factura · {resultsPassed} confirmada(s){resultHasErrors ? " · lote parcial o con incidencias" : ""}</CardTitle></CardHeader>
            <CardContent className="min-w-0 space-y-2">
              {resultsUncertain > 0 ? <p role="alert" className="rounded-md bg-amber-50 p-2 text-sm">{resultsUncertain} resultado(s) sin confirmación del servidor. Verifica el estado después de actualizar antes de reintentar.</p> : null}
              {resultsNotAttempted > 0 ? <p role="alert" className="rounded-md bg-amber-50 p-2 text-sm">{resultsNotAttempted} fila(s) no se enviaron porque una parte anterior quedó sin respuesta confirmada.</p> : null}
              {results.map((result) => {
                const success = result.status === "applied" || result.status === "already_applied";
                const label = result.status === "applied" ? "regularizada"
                  : result.status === "already_applied" ? "ya aplicada, sin nueva auditoría"
                    : result.status === "result_unconfirmed" ? "resultado no confirmado; revisar antes de reintentar"
                      : result.status === "not_attempted" ? "no enviada"
                        : result.status === "response_missing" ? "respuesta incompleta; revisar"
                          : "no aplicada";
                return (
                  <div key={`${result.id}-${result.part}`} className="flex min-w-0 flex-wrap items-start gap-2 text-sm [overflow-wrap:anywhere]">
                    {success ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" /> : <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />}
                    <span>Hito {result.id}: {label}. Parte {result.part}/{result.parts}.</span>
                    {result.message ? <span className="min-w-0 text-muted-foreground">{result.message}</span> : null}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ) : null}

        <p className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">El cambio solo afecta al desglose fiscal del hito y su exigible, con auditoría por fila. No modifica contrato, importe aprobado, estado, pagos, cobros parciales, ledger ni datos bancarios. La selección masiva conserva IDs y snapshots; si un lote supera 100, se previsualizan todas las partes antes de la confirmación explícita y se confirman secuencialmente, no de forma atómica. La QA física futura de selección de 20/todas en desktop y móvil sigue pendiente.</p>
      </div>
    </DashboardLayout>
  );
}
